<template>
  <div class="row text-justify-between mb-3 lh-sm">
    <div class="col-12 form-text mb-1" v-html="$t('detailsForm.forecast.b1')"></div>
  </div>
  <div class="row">
    <div class="col-12 col-md-12 col-lg-9 col-xl-10">
      <div class="row">
        <!-- Select for Data type -->
        <div class="col-12 col-md-12 col-lg-12"
             :class="{
              'col-xl-12': visualizationMode === constants.VISUALIZATION_MODE.SINGLE,
              'col-xl-6': visualizationMode === constants.VISUALIZATION_MODE.ADVANCED
            }">
          <div class="row">
            <div :class="{
                'col-6': visualizationMode === constants.VISUALIZATION_MODE.SINGLE,
                'col-12': visualizationMode === constants.VISUALIZATION_MODE.ADVANCED
            }">
              <label for="forecast_variable" class="form-label">
                <span class="me-1">{{ $t('InputSearchFormLabelTitle.Datatype') }}</span>
                <i class="bi bi-info-circle tooltip-init cursor-pointer me-1"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   @click="tooltipGlossaryOnClick"
                   :data-bs-title='`<small>${$t(tooltipGlossaryVariableKey())} <span class="fw-semibold">${tooltipGlossaryVariableName()}</span></small>`'></i>
                <div class="spinner-grow spinner-grow-sm" role="status" v-if="componentIsLoading === true">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </label>
            </div>
            <div class="col-6 text-end" v-if="visualizationMode === constants.VISUALIZATION_MODE.SINGLE">
              <a role="button" class="text-primary" @click="toggleVisualizationMode">
                <span class="small">{{ $t("Actions.ShowPercentiles") }}</span>
              </a>
            </div>
          </div>
          <select id="forecast_variable"
                  class="form-select"
                  v-model="variable"
                  :disabled="componentIsLoading === true || searchIsLoading === true">
            <option disabled value="" selected>{{ $t('formDataType.selectDataType') }}</option>
            <template v-for="(item, key) in this.variableMap">
              <option :value="key">{{ item.getNameI18N(this.$i18n.locale) }}</option>
            </template>
          </select>
        </div>

        <!-- Radio Buttons for Percentiles -->
        <div class="col-12 col-md-12 col-lg-12 col-xl-6 mt-2 mt-xl-0"
             v-show="visualizationMode === constants.VISUALIZATION_MODE.ADVANCED">
          <div class="row">
            <div class="col-6">
              <label class="form-label">{{ $t('InputSearchFormLabelTitle.Percentile') }}
                <i class="bi bi-info-circle tooltip-init cursor-pointer"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   @click="tooltipGlossaryOnClickPercentile"
                   :data-bs-title='`<small>${$t("tooltips.Percentile")}<br><br><span class="fst-italic">${$t("tooltips.GlossaryShow")}</span></small>`'></i>
              </label>
            </div>
            <div class="col-6 text-end">
              <a role="button" class="text-primary" @click="toggleVisualizationMode">
                <span class="small">{{ $t("Actions.HidePercentiles") }}</span>
              </a>
            </div>
          </div>
          <div class="btn-group d-flex" role="group">
            <input type="radio" name="forecast_percentile" class="btn-check" v-model="percentile"
                   :value="constants.PERCENTILES.BELOW_MEAN"
                   id="forecast_percentile02"
                   :disabled="componentIsLoading === true || this.percentileIsAvailable(constants.PERCENTILES.BELOW_MEAN) === false"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0 tooltip-init"
                   for="forecast_percentile02"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="$t('tooltips.Percentile20')">
              <small>{{ $t('InputSearchFormLabelValue.PercentileBelowAvgShort') }}</small>
            </label>
            <input type="radio" name="forecast_percentile" class="btn-check" v-model="percentile"
                   :value="constants.PERCENTILES.MEAN"
                   id="forecast_percentile05"
                   :disabled="componentIsLoading === true ||
               this.percentileIsAvailable(constants.PERCENTILES.MEAN) === false ||
              this.searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0 tooltip-init"
                   for="forecast_percentile05"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="$t('tooltips.Percentile50')">
              <small>{{ $t('InputSearchFormLabelValue.PercentileOnAverageShort') }}</small>
            </label>

            <input type="radio" name="forecast_percentile" class="btn-check" v-model="percentile"
                   :value="constants.PERCENTILES.ABOVE_MEAN"
                   id="forecast_percentile08"
                   :disabled="componentIsLoading === true ||
               this.percentileIsAvailable(constants.PERCENTILES.ABOVE_MEAN) === false ||
               this.searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0 tooltip-init"
                   for="forecast_percentile08"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="$t('tooltips.Percentile80')">
              <small>{{ $t('InputSearchFormLabelValue.PercentileAboveAvgShort') }}</small>
            </label>
          </div>
        </div>

        <!-- Creation month -->
        <div class="col-12 col-md-6 col-lg-6 mt-2">
          <label for="startDatePicker" class="form-label">{{ $t('InputSearchFormLabelTitle.CreationMonth') }}
            <i class="bi bi-info-circle tooltip-init"
               data-bs-toggle="tooltip"
               data-bs-html="true"
               :data-bs-title="`<small>${$t('tooltips.CreationMonth')}</small>`"></i>
          </label>
          <VueDatePicker
              id="startDatePicker"
              ref="datePickerCreationMonth"
              v-model="creationMonth"
              @update:model-value="onCreationMonthChange"
              format="MMM/yyy"
              :format-locale="localeToFuncLocale(this.$i18n.locale)"
              auto-apply
              month-picker
              year-picker
              prevent-min-max-navigation
              :clearable="false"
              :teleport="'#dashboardCalendar'"
              :locale="this.$i18n.locale"
              :disabled="componentIsLoading === true || creationMonthConfiguration === null || this.searchIsLoading === true"
              :start-date="creationMonthConfiguration !== null ? creationMonthConfiguration.focusStartDate : null"
              :min-date="creationMonthConfiguration !== null ? creationMonthConfiguration.minDate : null"
              :max-date="creationMonthConfiguration !== null ? creationMonthConfiguration.maxDate : null"
              :year-range="creationMonthConfiguration !== null ? creationMonthConfiguration.availableYears : null"
              :allowed-dates="creationMonthConfiguration !== null ? creationMonthConfiguration.availableDates : null"
              :disabled-dates="creationMonthConfiguration !== null ? creationMonthConfiguration.missingDates : null"
          />
        </div>

        <!-- Forecast month -->
        <div class="col-12 col-md-6 col-lg-6 mt-2">
          <label for="endDatePicker" class="form-label">{{ $t('InputSearchFormLabelTitle.ForecastMonth') }}
            <i class="bi bi-info-circle tooltip-init"
               data-bs-toggle="tooltip"
               data-bs-html="true"
               :data-bs-title="`<small>${$t('tooltips.ForecastMonth')}</small>`"></i>
          </label>
          <VueDatePicker
              id="endDatePicker"
              ref="datePickerForecastMonth"
              v-model="forecastMonth"
              format="MMM/yyy"
              :format-locale="localeToFuncLocale(this.$i18n.locale)"
              auto-apply
              month-picker
              year-picker
              prevent-min-max-navigation
              :clearable="false"
              :teleport="'#dashboardCalendar'"
              :locale="this.$i18n.locale"
              :disabled="componentIsLoading === true || forecastMonthConfiguration === null || searchIsLoading === true"
              :start-date="forecastMonthConfiguration !== null ? forecastMonthConfiguration.focusStartDate : null"
              :min-date="forecastMonthConfiguration !== null ? forecastMonthConfiguration.minDate : null"
              :max-date="forecastMonthConfiguration !== null ? forecastMonthConfiguration.maxDate : null"
              :year-range="forecastMonthConfiguration !== null ? forecastMonthConfiguration.availableYears : null"
              :allowed-dates="forecastMonthConfiguration !== null ? forecastMonthConfiguration.availableDates : null"
              :disabled-dates="forecastMonthConfiguration !== null ? forecastMonthConfiguration.missingDates : null"
          />
        </div>
      </div>
    </div>
    <!-- Explore button -->
    <div class="col-12 col-md-12 col-lg-3 col-xl-2 mt-3 mt-lg-0 align-self-end">
      <!-- Search Button -->
      <button type="button" class="btn btn-success w-100" :disabled="isSearchDisabled"
              @click="onSearchClick">
        <i v-if="propBlockComponent === true" class="bi bi-pause-circle pe-2"></i>
        <template v-if="propBlockComponent === false">
          <div v-if="searchIsLoading === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i v-if="searchIsLoading === false" class="bi pe-2"
             :class="{
              'bi-compass': isLastUUIDVisible === false,
              'bi-check-circle-fill': isLastUUIDVisible === true,
            }"></i>
        </template>
        <span class="small">{{ $t('ExploreButtonSearchForm.Explore') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Storage from "@/libs/Storage";
import constants from "@/libs/Constants";
import '@vuepic/vue-datepicker/dist/main.css';
import ApiConnector from "@/libs/ApiConnection";
import {localeToFuncLocale} from "@/libs/locales";
import VueDatePicker from '@vuepic/vue-datepicker';
import GroupErrorTranslation from '@/libs/locales/errors';
import {VariableColorConfigurationInterface} from "@/libs/Colors";
import {VariableConfiguration, VisualizationForecastData} from '@/libs/VisualizationData';
import {
  disposeTooltipsByClass,
  setupTooltipsByClass,
  yearMonthToDate,
  monthYearCalendarProperties,
  yearMonthToStr
} from "@/libs/Helpers";

export default {
  components: {VueDatePicker},
  emits: ['onSearchClick', 'onSearchClickError', 'onSearchClickWait', 'afterSearchClickResult', 'tooltipGlossaryOnClick'],
  props: {
    propInitializeTooltips: Boolean,
    propLastUuid: String,
    propBlockComponent: Boolean
  },
  watch: {
    variable(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.creationMonth = null;
        this.forecastMonth = null;
      }
    }
  },
  data() {
    return {
      localeToFuncLocale,
      //Input fields
      variable: '',
      percentile: constants.PERCENTILES.MEAN,
      creationMonth: null,
      forecastMonth: null,

      visualizationMode: constants.VISUALIZATION_MODE.SINGLE,

      //Inputs
      variableMap: {}, //List of ID -> variable names
      variablesDates: {}, //Calendar configuration for Creation date for each variable
      variablesForecastDates: {}, //Calendar configuration for Creation date + forecast date for each variable
      variablesForecastStepMap: {}, //Mapping between dates and step number for forecast steps
      percentilesAvailable: [],

      //Constants
      constants: constants,

      //Loading
      componentIsLoading: true,  // Loading state for component
      searchIsLoading: false     // Loading state for search button
    }
  },
  beforeMount() {
    let forecastLayersKey = 'ForecastLayers';
    let forecastLayers = Storage.get(forecastLayersKey);

    if (forecastLayers !== null) {
      this.processLayers(forecastLayers);
      this.componentIsLoading = false;
    } else
      ApiConnector.getForecastLayers().then((result) => {
        result.json().then(output => {
          //Store item locally (if variable is set)
          let secondsStorage = parseInt(process.env.VUE_APP_KEEP_LAYER_DATA_IN_CACHE_SECONDS);
          if (isNaN(secondsStorage) === false && secondsStorage > 0)
            Storage.set(forecastLayersKey, output, secondsStorage);

          this.processLayers(output);
        });
      }).catch((error) => {
        // Translate message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.componentIsLoading = false;
      });
  },
  mounted() {
    if (this.propInitializeTooltips === true)
      this.tooltipsSetup();
  },
  beforeUnmount() {
    if (this.propInitializeTooltips === true)
      this.tooltipsDispose();
  },
  computed: {
    isVariableSet() {
      return this.variableMap[this.variable] !== undefined;
    },
    creationMonthStr() {
      return this.creationMonth !== null ? yearMonthToStr(this.creationMonth.year, this.creationMonth.month + 1) : null;
    },
    forecastMonthStr() {
      return this.forecastMonth !== null ? yearMonthToStr(this.forecastMonth.year, this.forecastMonth.month + 1) : null;
    },
    isCreationMonthYearSet() {
      if (this.creationMonthStr === null)
        return false;
      return this.variablesForecastStepMap[this.variable][this.creationMonthStr] !== undefined;
    },
    isForecastMonthYearSet() {
      if (this.forecastMonthStr === null)
        return false;
      return this.variablesForecastStepMap[this.variable][this.creationMonthStr][this.forecastMonthStr] !== undefined;
    },
    isFormComplete() {
      return this.isVariableSet === true &&
          this.isCreationMonthYearSet !== false &&
          this.isForecastMonthYearSet !== false &&
          this.percentileIsAvailable(this.percentile) !== false;
    },
    isLastUUIDVisible() {
      let dataObject = this.createDataObject({});
      if (this.propLastUuid === null || dataObject === null)
        return false;

      return dataObject.uuid === this.propLastUuid
    },
    isSearchDisabled() {
      return this.isFormComplete === false ||
          this.searchIsLoading === true ||
          this.isLastUUIDVisible === true ||
          this.propBlockComponent === true;
    },
    creationMonthConfiguration() {
      return this.isVariableSet === true ?
          this.variablesDates[this.variable] || null :
          null;
    },
    forecastMonthStep() {
      let stepValue = null;
      if (this.isVariableSet === true && this.creationMonthStr !== null && this.forecastMonthStr !== null) {
        let monthYearStr = this.creationMonthStr;
        let monthYearForecastStr = this.forecastMonthStr;

        stepValue = this.variablesForecastStepMap[this.variable][monthYearStr][monthYearForecastStr];
        stepValue = stepValue === undefined ? null : stepValue;
      }
      return stepValue;
    },
    forecastMonthConfiguration() {
      let configuration = null;
      if (this.isVariableSet === true && this.creationMonthStr !== null) {
        let monthYearStr = this.creationMonthStr;
        configuration = this.variablesForecastDates[this.variable][monthYearStr];
        configuration = configuration === undefined ? null : configuration;
      }
      return configuration;
    },
  },
  methods: {
    processLayers(output) {
      this.percentilesAvailable = output.percentiles || [];

      //Variable -> Creation dates {} -> Forecast date configuration
      let variablesForecastDates = {};
      let variablesForecastDatesMap = {};

      //Iteration for each variable
      let outputVariables = output.variables || [];
      outputVariables.forEach(item => {
        let itemId = item.id;

        /**
         * Colors - layers + percentiles
         */
        let itemColorScaleData = item.color_scale;
        let colorInterface = new VariableColorConfigurationInterface();
        let variableColorPalette = colorInterface.newObject(itemColorScaleData.type, itemColorScaleData.values);

        let percentileColors = {};
        let itemColorPercentile = item.color_percentile;
        if (itemColorPercentile !== undefined && itemColorPercentile.length > 0)
          itemColorPercentile.forEach(itemColor => {
            percentileColors[itemColor.name] = itemColor.rgb
          })

        this.variableMap[itemId] = new VariableConfiguration(
            itemId,
            {
              en: item.name.en,
              it: item.name.it
            },
            variableColorPalette,
            item.measure_unit_abbreviation,
            item.decimals || 0,
            item.allows_multiple_areas,
            percentileColors,
            itemColorScaleData.background_colors
        )

        let variableDates = [];
        let forecastDates = {};
        let forecastDatesMap = {};

        //Iteration for each creation date of variable
        Object.keys(item.dates).forEach(monthYear => {
          let monthYearSteps = item.dates[monthYear];
          let creationDateDT = yearMonthToDate(monthYear);

          if (creationDateDT !== null) {
            //Iteration for each forecast step
            let creationDateForecastMap = {};
            let creationDateForecastDates = [];

            Object.keys(monthYearSteps).forEach(forecastYearMonth => {
              let forecastDateDT = yearMonthToDate(forecastYearMonth);
              if (forecastDateDT !== null) {
                creationDateForecastMap[forecastYearMonth] = monthYearSteps[forecastYearMonth];
                creationDateForecastDates.push(forecastYearMonth);
              }
            });

            if (creationDateForecastDates.length > 0) {
              //Keep only the calendar configuration for forecast dates of the creation date
              forecastDates[monthYear] = monthYearCalendarProperties(
                  creationDateForecastDates,
                  true
              );

              forecastDatesMap[monthYear] = creationDateForecastMap;
            }
          }
          variableDates.push(monthYear);
        });

        //Calendar configuration for each creation date + forecast date
        variablesForecastDates[itemId] = forecastDates;

        //Map of forecast dates with step number for variable
        variablesForecastDatesMap[itemId] = forecastDatesMap;

        //Build calendar properties based on list of months
        this.variablesDates[itemId] = monthYearCalendarProperties(variableDates)
      });

      this.variablesForecastDates = variablesForecastDates;
      this.variablesForecastStepMap = variablesForecastDatesMap;
    },
    createDataObject(geoJson) {
      if (this.isVariableSet === false ||
          this.isCreationMonthYearSet === false ||
          this.isForecastMonthYearSet === false ||
          this.percentileIsAvailable(this.percentile) === false)
        return null;

      // Create the search data object from the form fields
      return new VisualizationForecastData(
          //Meta-data
          this.variableMap[this.variable],
          this.creationMonth.month + 1,
          this.creationMonth.year,
          this.forecastMonth.month + 1,
          this.forecastMonth.year,
          this.percentile,
          // Loaded data
          geoJson,
          this.visualizationMode
      );
    },
    onCreationMonthChange() {
      this.forecastMonth = null;
    },
    percentileIsAvailable(percentile) {
      return this.percentilesAvailable.indexOf(percentile) !== -1;
    },
    onSearchClick() {
      if (this.isSearchDisabled) {
        // Translate message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(constants.STATUS.PARAMS_MISSING, this.$i18n));
        return;
      }

      this.searchIsLoading = true;
      this.$emit('onSearchClickWait');

      ApiConnector.postForecastQuery(this.variable, this.percentile, this.creationMonthStr, this.forecastMonthStr).then((result) => {
        result.json().then(output => {
          // Create the search data object from the form fields
          const searchData = this.createDataObject(output);

          // Emit the search data to the parent component
          this.$emit('onSearchClick', searchData);

          /*Timeout to wait some seconds before the filter window is hidden*/
          setTimeout(() => {
            this.searchIsLoading = false;
            this.$emit('afterSearchClickResult');
          }, 700);  // Stop loading

        });
      }).catch((error) => {
        this.searchIsLoading = false;
        this.$emit('afterSearchClickResult');

        //Translate error
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
      });
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-init');
    },
    tooltipGlossaryVariableKey() {
      return (this.isVariableSet) ? 'tooltips.GlossaryShowVariable' : 'tooltips.GlossaryShowVariablesAvailable';
    },
    tooltipGlossaryVariableName() {
      return this.isVariableSet ? this.variableMap[this.variable].getNameI18N(this.$i18n.locale) : '';
    },
    tooltipGlossaryOnClickPercentile() {
      this.$emit('tooltipGlossaryOnClick', 'percentile');
    },
    tooltipGlossaryOnClick() {
      this.$emit('tooltipGlossaryOnClick', this.variable);
    },
    toggleVisualizationMode() {
      if (this.visualizationMode === constants.VISUALIZATION_MODE.SINGLE) {
        this.visualizationMode = constants.VISUALIZATION_MODE.ADVANCED;
      } else {
        this.visualizationMode = constants.VISUALIZATION_MODE.SINGLE;

        //In simple mode, the percentile has to be the average
        this.percentile = constants.PERCENTILES.MEAN;
      }
    }
  },
  updated() {
    this.$refs.datePickerCreationMonth.parseModel();
    this.$refs.datePickerForecastMonth.parseModel();
    this.tooltipsSetup();
  }
}
</script>